/**
 * Agreement 组件
 * 从 AZ_AI_MVR_H5/src/pages/OpsMate/components/Agreement/index.tsx 迁移
 */
import { ConfigProvider, theme, Typography } from 'antd';
import Link from 'antd/es/typography/Link';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

export default function Agreement() {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  return (
    <ConfigProvider
      theme={{
        token: {
          colorLink: token.colorPrimary,
        },
      }}
    >
      <Typography>
        <Title level={3}>{t('opsmate.agreement.title')}</Title>
        <Paragraph>
          By using AZ OpsMate, you confirm that you have read and understood the Terms of
          Use.
        </Paragraph>
        <Paragraph strong>
          For study-related questions, the established processes must be followed, and confirmation must be obtained from the relevant personnel;
          For business matters involving external communications, please contact the corresponding team for secondary confirmation.
          For issues related to operational quality, please be sure to submit your inquiry through the{' '}
          <Link href="https://forms.office.com/e/WEziwqeY4W" target="_blank" rel="noopener noreferrer">
            CQM Helpdesk
          </Link>
          .
        </Paragraph>
        <Paragraph strong>
          涉及研究相关问题，必须遵照原业务流程与相关人员进行确认；
          涉及对外沟通的业务问题，请联系对应团队进行二次确认; 涉及运营质量相关问题，请务必通过{' '}
          <Link href="https://forms.office.com/e/WEziwqeY4W" target="_blank" rel="noopener noreferrer">
            CQM Helpdesk
          </Link>{' '}
          进行提问
        </Paragraph>
        <Paragraph>
          All individual users are accountable for using AZ OpsMate in a safe, responsible and
          compliant way.
        </Paragraph>
        <Paragraph>
          All data processed via AZ OpsMate is safe and secure on AZ's internal network.
        </Paragraph>
        <Paragraph>
          All input, output, log and usage data is retained for 6 months and then securely
          deleted. Access to AZ OpsMate log data is strictly limited to exceptional circumstances,
          such as legal investigations or eDiscovery requests, requiring prior review and approval,
          Aggregated, anonymized usage data is collected for reporting and service
          improvement.
        </Paragraph>
        <Title level={4}>{t('opsmate.agreement.tocTitle')}</Title>
        <Paragraph>
          <ol>
            <li>5 Key Requirements for Responsible Use of AZ OpsMate</li>
            <li>Uploading content, documents and images into AZ OpsMate</li>
            <li>Human oversight when using AZ OpsMate</li>
            <li>Privacy and security</li>
            <li>Approved countries and use in China</li>
            <li>Prohibited use cases</li>
            <li>Additional guidance and key contacts</li>
          </ol>
        </Paragraph>
        <Title level={4}>1. 5 Key Requirements for Responsible Use of AZ OpsMate</Title>
        <Paragraph>
          <>
            <Title level={5}>1. Protect AZ's data and information</Title>
            <Paragraph>
              <ul>
                <li>
                  You can upload AZ data and information, including Strictly Confidential
                  materials, into AZ OpsMate.
                </li>
              </ul>
            </Paragraph>
            <Title level={5}>2. Safeguard privacy</Title>
            <Paragraph>
              <ul>
                <li>
                  Do not upload or input sensitive personal data, unless you have an approved
                  PIA.
                </li>
                <li>
                  Seek advice from Privacy and Enterprise AI Governance before pursuing high
                  risk AI use cases or before using sensitive personal data.
                </li>
              </ul>
            </Paragraph>
            <Title level={5}>3. Respect copyright</Title>
            <Paragraph>
              <ul>
                <li>
                  Do not upload documents, data or images which are copyright protected or do
                  not have the appropriate license or permissions.
                </li>
                <li>
                  Always check the license for text and data mining permissions, before uploading
                  any journal articles.
                </li>
              </ul>
            </Paragraph>
            <Title level={5}>4. Keep the human-in-the-loop</Title>
            <Paragraph>
              <ul>
                <li>
                  Always verify AI-generated content for accuracy, reliability and safety before
                  sharing, publishing or using it.
                </li>
                <li>
                  Don't use AI to automate important, material, or legally significant decision
                  making which impacts individuals.
                </li>
              </ul>
            </Paragraph>
            <Title level={5}>5. Be vigilant</Title>
            <Paragraph>
              <ul>
                <li>
                  Report feedback, concerns and malfunctions, including any harmful or toxic
                  content, as well as information incidents and data breaches.
                </li>
              </ul>
            </Paragraph>
          </>
          <>
            <Title level={5}>2. Uploading content, documents and images into AZ OpsMate</Title>
            <Paragraph>
              AZ OpsMate is a generative AI powered application which generates text-based
              outputs and content, in response to user inputs, queries and uploads.
            </Paragraph>
            <Paragraph>
              Users can input a wide range of content and material into AZ OpsMate as prompts or
              uploads. This includes text and text excerpts, images, documents and files.
            </Paragraph>
            <Paragraph>
              However, there are important restrictions on the content and materials which users can
              upload.
            </Paragraph>
            <Paragraph>
              Adherence to these restrictions is critical for ensuring that AZ protects its confidential
              data and information, whilst complying with applicable laws and regulations.
            </Paragraph>
            <Title level={5} italic>Uploading AZ data and information</Title>
            <Paragraph>
              <ul>
                <li>
                  In general, documents, information and material classified as 'Strictly
                  Confidential', 'Company Restricted' and 'Public' can be uploaded into AZ
                  OpsMate, granted that caution is exercised in relation to the permissions and
                  license that you may have in relation to such documents, information and
                  materials. However, the restrictions outlined below must be adhered to.
                </li>
              </ul>
            </Paragraph>
            <Title level={5} italic>Uploading Personal Data</Title>
            <Paragraph>
              <ul>
                <li>
                  In general, you can freely upload documents and material which contains
                  Personal Data into AZ OpsMate
                  <ul className="circle">
                    <li>
                      Always consider{' '}
                      <Link
                        target="_blank"
                        href="https://astrazeneca-eqv.veevavault.com/ui/#doc_info/1087863/6/0"
                      >
                        AZ's Global Privacy Standard
                      </Link>{' '}
                      and ensure you have a legal basis before using personal data.
                    </li>
                    <li>
                      Please keep in mind that local data protection laws will apply depending
                      on your jurisdiction and that of the individual's whose Personal Data is
                      being processed.
                    </li>
                  </ul>
                </li>
                <li>
                  However, you must not upload documents, information or material which
                  contains Sensitive Personal Data, unless you have a full, "Reviewed-Remediation
                  Acceptable" status PIA and AZ AI Risk Assessment for this specific use case.
                </li>
                <li>
                  This is because the use of sensitive personal data is subject to regulatory
                  restriction under privacy and data protection laws. Sensitive personal data
                  includes:
                  <ul className="circle">
                    <li>financial, banking and salary data;</li>
                    <li>data revealing racial or ethnic origin;</li>
                    <li>data revealing political opinions;</li>
                    <li>data revealing religious or philosophical beliefs;</li>
                    <li>data revealing trade union membership;</li>
                    <li>genetic data;</li>
                    <li>biometric data (e.g., faces);</li>
                    <li>health data and patient health records;</li>
                    <li>data concerning a person's sex life or sexual orientation;</li>
                    <li>data regarding criminal convictions or background checks; and</li>
                    <li>personal government identifiers.</li>
                  </ul>
                </li>
                <li>
                  You must conduct a full PIA before pursuing any use cases which involve the
                  processing of Sensitive Personal Data.
                </li>
              </ul>
            </Paragraph>
            <Title level={5} italic>Uploading external data, publications and images</Title>
            <Paragraph>
              <ul>
                <li>
                  You must not upload or input documents, images or material with external data,
                  unless AZ has the appropriate legal right to use this external data for AI processing.
                </li>
                <li>
                  External data includes, but is not limited to, public datasets, open-source code,
                  website content, scientific journal articles, artistic images etc.
                </li>
                <li>
                  Before uploading any external data, you must check whether AZ has the legal
                  right to use the corresponding external data in the context of AI processing.
                </li>
                <li>
                  For journal articles, you must check whether the license allows 'text and data
                  mining', by using the{' '}
                  <Link target="_blank" href="https://www.reprintsdesk.com/userv3/userdashboard.aspx#/">
                    AZ Article Galaxy
                  </Link>
                </li>
              </ul>
            </Paragraph>
          </>
          <>
            <Title level={5}>3. Human oversight when using AZ OpsMate</Title>
            <Paragraph>
              All generative AI applications, including AZ OpsMate, can generate content and
              information which is inaccurate, misleading, inappropriate or even harmful.
            </Paragraph>
            <Paragraph>
              To ensure safe and responsible use of generative AI, meaningful human oversight must
              always be in place. The following requirements must be followed:
            </Paragraph>
            <Paragraph>
              <ul>
                <li>You must review and verify AI-generated outputs for accuracy, truthfulness, safety and appropriateness.</li>
                <li>Appropriate review and approval processes must be in place when AZ OpsMate is used to produce external-facing documents.</li>
                <li>You must review and verify AI-generated code for security vulnerabilities, bugs and other issues.</li>
                <li>You must report any harmful, offensive or discriminatory content via the feedback form.</li>
                <li>If AZ OpsMate provides personal information you shouldn't access, report it via the Information Incident process.</li>
                <li>You must not use AI-generated outputs as the sole basis for decisions regarding individuals with material or legal impact.</li>
                <li>AZ OpsMate must not be used to automate important decision-making.</li>
                <li>You must only use AZ OpsMate for business appropriate use cases aligned to AZ's Values and Code of Ethics.</li>
              </ul>
            </Paragraph>
          </>
          <>
            <Title level={5}>4. Information Security and Data Retention</Title>
            <Paragraph>
              All inputs uploaded into AZ OpsMate are processed securely. Data is stored securely
              and retained for 6 months.
            </Paragraph>
            <Paragraph>
              <ul>
                <li>AZ OpsMate must not be made available to non-AZ users.</li>
                <li>You must not share your AZ OpsMate login credentials with anyone.</li>
                <li>You must not share your AZ OpsMate chat history with any non-AZ individuals.</li>
              </ul>
            </Paragraph>
          </>
          <>
            <Title level={5}>5. Approved countries and China</Title>
            <Paragraph>
              AZ OpsMate must only be used by AZ users, in approved countries, who have been granted access.
            </Paragraph>
            <Paragraph>
              <ul>
                <li>AZ OpsMate is not promoted or shared outside of AZ in China; and</li>
                <li>AZ OpsMate must not be made available to or shared with non-AZ users in China.</li>
              </ul>
            </Paragraph>
          </>
          <>
            <Title level={5}>6. Prohibited use cases</Title>
            <Paragraph>
              You must not use AZ OpsMate to pursue any AI use cases which are prohibited under
              the EU AI Act. This includes:
            </Paragraph>
            <Paragraph>
              <ul>
                <li>Emotion recognition in the workplace and education</li>
                <li>Biometric categorisation to infer protected characteristics</li>
                <li>Creating facial recognition databases via untargeted scraping</li>
                <li>Causing harm by deploying subliminal or deceptive techniques</li>
                <li>Causing harm by exploiting vulnerabilities</li>
                <li>Social credit scoring systems</li>
                <li>Predictive policing</li>
              </ul>
            </Paragraph>
            <Paragraph>
              In case you have any questions or concerns, please contact the Enterprise AI
              Governance team.
            </Paragraph>
          </>
          <>
            <Title level={5}>7. Additional guidance and key contacts</Title>
            <Paragraph>
              <Text strong>Enterprise AI Governance </Text>
              <Text italic>For general questions on AI governance and compliance</Text>
            </Paragraph>
            <Paragraph>
              <ul>
                <li>
                  <Link
                    href="https://azusgb01--cms.vf.force.com/apex/Main?sname=Intranet&name=AIGovernance#/"
                    target="_blank"
                  >
                    AZ Enterprise AI Governance website
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://azusgb01--cms.vf.force.com/apex/Main?sname=Intranet&name=AI-Governance-Policy-Framework#/"
                    target="_blank"
                  >
                    AZ Global AI Standard
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://astrazeneca-eqv.veevavault.com/ui/#doc_info/1459793/2/0?anQS=page1&annotate=false"
                    target="_blank"
                  >
                    AZ Guidance on Employee and Business Use of Generative AI
                  </Link>
                </li>
                <li>
                  Email:{' '}
                  <Link href="mailto:aigovernance@astrazeneca.com">
                    aigovernance@astrazeneca.com
                  </Link>
                </li>
              </ul>
            </Paragraph>
            <Paragraph>
              <Text strong>External Content Team </Text>
              <Text> (Enterprise Data Office)</Text>
            </Paragraph>
            <Paragraph>
              <ul>
                <li>
                  If you have any doubts or questions regarding the use of external data and
                  journal articles, please contact:{' '}
                  <Link href="mailto:externalcontentteam@astrazeneca.com">
                    externalcontentteam@astrazeneca.com
                  </Link>
                </li>
              </ul>
            </Paragraph>
          </>
        </Paragraph>
      </Typography>
    </ConfigProvider>
  );
}
